import moment from 'moment';

/**
 * Parses a date string and returns a Date object localized to the user's browser timezone.
 * Uses moment.js for robust date parsing and supports various date formats.
 * Falls back to US Eastern timezone if browser timezone cannot be determined.
 *
 * @param {string} dateString - The date string to parse (ISO, RFC, Unix timestamp, etc.)
 * @param {string} [fallbackTimezone='America/New_York'] - Fallback timezone if browser timezone is unavailable
 * @returns {Date|string} - Localized Date object or string if parsing fails
 */
export const parseToLocalizedDate = (
  dateString,
  fallbackTimezone = 'America/New_York',
) => {
  if (!dateString || typeof dateString !== 'string') {
    return dateString;
  }

  try {
    let momentObj = null;

    // Try parsing with moment using common formats
    const formats = [
      // ISO formats
      moment.ISO_8601,
      // Common date formats
      'MM/DD/YYYY',
      'MM/DD/YYYY HH:mm',
      'MM/DD/YYYY hh:mm A',
      'MM/DD/YYYY HH:mm:ss',
      'MM/DD/YYYY hh:mm:ss A',
      'DD/MM/YYYY',
      'DD/MM/YYYY HH:mm',
      'DD/MM/YYYY hh:mm A',
      'DD/MM/YYYY HH:mm:ss',
      'DD/MM/YYYY hh:mm:ss A',
      'YYYY-MM-DD',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD HH:mm:ss',
      'MMM DD, YYYY',
      'MMM DD, YYYY HH:mm',
      'MMM DD, YYYY hh:mm A',
      'MMMM DD, YYYY',
      'MMMM DD, YYYY HH:mm',
      'MMMM DD, YYYY hh:mm A',
    ];

    // Try parsing with multiple formats
    momentObj = moment(dateString, formats, true);

    // If strict parsing fails, try lenient parsing
    if (!momentObj.isValid()) {
      momentObj = moment(dateString);
    }

    // Try parsing as Unix timestamp if still invalid
    if (!momentObj.isValid()) {
      const timestamp = parseInt(dateString, 10);
      if (!Number.isNaN(timestamp)) {
        // Handle both seconds and milliseconds timestamps
        momentObj = timestamp < 10000000000 ? moment.unix(timestamp) : moment(timestamp);
      }
    }

    // If all parsing attempts failed
    if (!momentObj || !momentObj.isValid()) {
      return dateString;
    }

    // Get user's browser timezone or use fallback
    let userTimezone;
    try {
      userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      userTimezone = fallbackTimezone;
    }

    if (!userTimezone) {
      userTimezone = fallbackTimezone;
    }

    // Convert to user's local timezone and return as Date object
    return momentObj.toDate();
  } catch (error) {
    return dateString;
  }
};

/**
 * Helper function to check if a date string or Date object is valid
 * Uses moment.js for more robust validation
 * @param {string|Date|moment.Moment} date - Date to validate
 * @returns {boolean} - True if valid date
 */
export const isValidDate = (date) => {
  if (moment.isMoment(date)) {
    return date.isValid();
  }
  if (date instanceof Date) {
    return moment(date).isValid();
  }
  if (typeof date === 'string') {
    return moment(date).isValid();
  }
  return false;
};

/**
 * Adds minutes to a date and returns a new Date object
 * Uses moment.js for more reliable date arithmetic and handles DST transitions
 * @param {Date|string|moment.Moment} date - Base date
 * @param {number} minutes - Minutes to add
 * @returns {Date|null} - New date with added minutes, accounting for DST
 */
export const addMinutesToDate = (date, minutes) => {
  const momentDate = moment(date);
  if (!momentDate.isValid()) {
    return null;
  }

  // Add minutes using moment.js which properly handles DST transitions
  // Moment automatically adjusts for DST changes during the time span
  const resultMoment = momentDate.clone().add(minutes, 'minutes');

  return resultMoment.toDate();
};

/**
 * Gets the current user's timezone or falls back to US Eastern
 * @param {string} [fallbackTimezone='America/New_York'] - Fallback timezone
 * @returns {string} - Timezone identifier
 */
export const getUserTimezone = (fallbackTimezone = 'America/New_York') => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || fallbackTimezone;
  } catch (e) {
    return fallbackTimezone;
  }
};

/**
 * Gets the timezone abbreviation for a specific date, accounting for DST
 * @param {Date} date - The date to get timezone info for
 * @returns {string} - Timezone abbreviation (e.g., EDT, EST, PDT, PST)
 */
export const getDSTAwareTimezoneAbbr = (date) => {
  try {
    // Use Intl.DateTimeFormat to get timezone info for the specific date
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: getUserTimezone(),
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find((part) => part.type === 'timeZoneName');

    if (timeZonePart) {
      return timeZonePart.value;
    }

    // Fallback: use the old method if formatToParts fails
    return date
      .toLocaleDateString('en-US', {
        timeZoneName: 'short',
      })
      .split(', ')
      .pop();
  } catch (error) {
    // Ultimate fallback
    return date.toString().match(/\(([^)]+)\)$/)?.[1] || 'UTC';
  }
};

/**
 * Formats a date with timezone information using moment.js
 * Properly handles DST by using the actual date for timezone abbreviation
 * @param {Date|string|moment.Moment} date - Date to format
 * @param {string} [format='MMMM DD, YYYY, h:mm A'] - Custom format string
 * @returns {string} - Formatted date string with timezone
 */
export const formatDateWithTimezone = (date, format = 'MMMM DD, YYYY, h:mm A') => {
  const momentDate = moment(date);
  if (!momentDate.isValid()) {
    return '';
  }

  // Convert to native Date to get accurate timezone info for the specific date
  const nativeDate = momentDate.toDate();

  // Get timezone abbreviation for the specific date (accounts for DST)
  const timeZoneAbbr = getDSTAwareTimezoneAbbr(nativeDate);

  return `${momentDate.format(format)} ${timeZoneAbbr}`;
};

/**
 * Calculates the minute difference between two date objects, accounting for timezones and DST
 * @param {Date|string|moment.Moment} startDate - The start date
 * @param {Date|string|moment.Moment} endDate - The end date
 * @returns {number|null} - Difference in minutes (positive if endDate is after startDate, negative if before), or null if invalid dates
 */
export const getMinuteDifference = (startDate, endDate) => {
  const startMoment = moment(startDate);
  const endMoment = moment(endDate);

  if (!startMoment.isValid() || !endMoment.isValid()) {
    return null;
  }

  // Moment.js automatically handles timezone differences and DST transitions
  // when calculating the difference between two dates
  return endMoment.diff(startMoment, 'minutes');
};

/**
 * Parses API calendar dates (YYYY-MM-DD) as local calendar dates.
 * `new Date("YYYY-MM-DD")` treats the value as UTC midnight, which displays
 * one day early in US timezones.
 *
 * @param {string|Date|null|undefined} dateInput
 * @returns {Date|null}
 */
export const parseCalendarDate = (dateInput) => {
  if (!dateInput) {
    return null;
  }

  if (dateInput instanceof Date) {
    return moment(dateInput).isValid() ? dateInput : null;
  }

  if (typeof dateInput !== 'string') {
    return null;
  }

  const dateOnlyMatch = dateInput.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnlyMatch) {
    const parsed = moment(dateOnlyMatch[1], 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  const parsed = moment(dateInput);
  return parsed.isValid() ? parsed.toDate() : null;
};

/**
 * Formats a Date for Django DateField / API payloads (YYYY-MM-DD).
 *
 * @param {Date|string|moment.Moment|null|undefined} date
 * @returns {string}
 */
export const formatCalendarDate = (date) => {
  if (!date) {
    return '';
  }

  const parsed = moment(date);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

/**
 * Calendar-day difference from the given date to today (positive = date is in the past).
 *
 * @param {string|Date} dateInput
 * @returns {number}
 */
export const daysSinceCalendarDate = (dateInput) => {
  const date = parseCalendarDate(dateInput);
  if (!date) {
    return 0;
  }

  return moment().startOf('day').diff(moment(date).startOf('day'), 'days');
};

/**
 * Whether a calendar date is today or in the past (local timezone).
 *
 * @param {string|Date} dateInput
 * @returns {boolean}
 */
export const hasCalendarDateStarted = (dateInput) =>
  daysSinceCalendarDate(dateInput) >= 0;
